import type { Order, OrderFill, Trade } from '../../types';
import type { MessageCallbacks } from './types';

const STATUS_PRIORITY: Record<string, number> = { filled: 4, partially_filled: 3, canceled: 2, rejected: 2, new: 1 };

export function handleOrderAck(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.type === 'ORDER_ACK' || obj?.kind === 'ORDER_ACK')) return false;

  const orderAck = obj.order || obj;
  const clientOrderId = orderAck.clientOrderId || orderAck.id || orderAck.orderId || obj.orderId;
  const exchangeOrderId = orderAck.orderId || obj.orderId;
  const symbol = (orderAck.symbol || obj.symbol || '').toUpperCase();

  if (clientOrderId && symbol) {
    const statusFromMessage = (orderAck.status || obj.status || '').toUpperCase();
    let orderStatus: Order['status'] = 'new';
    if (statusFromMessage === 'FILLED' || statusFromMessage === '2') orderStatus = 'filled';
    else if (statusFromMessage === 'PARTIALLY_FILLED' || statusFromMessage === '1') orderStatus = 'partially_filled';
    else if (statusFromMessage === 'REJECTED' || statusFromMessage === '8') orderStatus = 'rejected';

    const orderQty = Number(orderAck.quantity || orderAck.qty || obj.quantity || obj.qty || 0);
    const newOrder: Order = {
      id: clientOrderId,
      exchangeOrderId: exchangeOrderId || undefined,
      symbol,
      side: ((orderAck.side || obj.side)?.toUpperCase() === 'SELL' || (orderAck.side || obj.side)?.toLowerCase() === 'sell') ? 'sell' : 'buy',
      qty: orderQty,
      price: Number(orderAck.price || orderAck.limitPrice || obj.price || obj.limitPrice || 0),
      status: orderStatus,
      time: orderAck.time || orderAck.timestamp || obj.time || obj.timestamp || new Date().toISOString(),
      fills: [],
      cumQty: 0,
      leavesQty: orderQty,
      timeInForce: orderAck.timeInForce != null ? Number(orderAck.timeInForce) : undefined,
      expireDate: orderAck.expireDate ?? undefined,
      expireTime: orderAck.expireTime ?? undefined,
    };

    cb.setOrders(prev => {
      const existingIndex = prev.findIndex(o => o.id === clientOrderId);
      if (existingIndex >= 0) {
        const updated = [...prev];
        const existing = updated[existingIndex];
        const existingPriority = STATUS_PRIORITY[existing.status] ?? 0;
        const newPriority = STATUS_PRIORITY[newOrder.status] ?? 0;
        updated[existingIndex] = {
          ...existing,
          ...newOrder,
          status: existingPriority > newPriority ? existing.status : newOrder.status,
          fills: existing.fills || [],
          cumQty: existing.cumQty ?? 0,
          leavesQty: existing.leavesQty ?? newOrder.leavesQty,
          // preserve TIF fields from existing unless the message explicitly carries them
          timeInForce: newOrder.timeInForce ?? existing.timeInForce,
          expireDate: newOrder.expireDate ?? existing.expireDate,
          expireTime: newOrder.expireTime ?? existing.expireTime,
        };
        return updated;
      }
      return [newOrder, ...prev];
    });
  }
  return true;
}

export function handleOrderReject(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.type === 'ORDER_REJECT' || obj?.type === 'REJECT' || obj?.kind === 'ORDER_REJECT' || obj?.kind === 'REJECT')) return false;

  const reject = obj.order || obj.rejection || obj;
  const clientOrderId = reject.clientOrderId || reject.id || reject.orderId;

  if (clientOrderId) {
    cb.setOrders(prev => prev.map(o => o.id === clientOrderId ? { ...o, status: 'rejected' } : o));
  }
  return true;
}

export function handleExecutionReport(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.type === 'EXECUTION_REPORT' || obj?.type === 'FILL' || obj?.type === 'TRADE' ||
        obj?.kind === 'EXECUTION_REPORT' || obj?.kind === 'FILL' || obj?.kind === 'TRADE')) return false;

  const exec = obj.execution || obj.fill || obj.trade || obj;
  const clientOrderId = exec.clientOrderId || exec.orderId || exec.id || obj.orderId;
  const symbol = (exec.symbol || obj.symbol || '').toUpperCase();
  const execPrice = Number(exec.price || exec.execPrice || exec.fillPrice || obj.price || 0);
  const cumQty = Number(exec.cumQty || exec.execQty || exec.fillQty || obj.cumQty || 0);
  const leavesQty = Number(exec.leavesQty || obj.leavesQty || 0);
  const fillQty = Number(exec.quantity || obj.quantity || 0);
  const execQty = fillQty > 0 ? fillQty : cumQty;
  const execSide = ((exec.side || obj.side)?.toUpperCase() === 'SELL' || (exec.side || obj.side) === 'sell') ? 'sell' : 'buy';
  const fillTime = exec.time || exec.timestamp || obj.time || obj.timestamp || new Date().toISOString();
  const fillValue = exec.value || (execPrice * execQty);

  const ordStatusValue = exec.ordStatus !== undefined ? exec.ordStatus : (obj.ordStatus !== undefined ? obj.ordStatus : undefined);
  const ordStatusMap: Record<string, string> = { '0': 'new', '1': 'partially_filled', '2': 'filled', '4': 'cancelled', '8': 'rejected' };
  let orderStatus = ordStatusValue !== undefined
    ? (ordStatusMap[String(ordStatusValue)] || 'new')
    : (exec.status || exec.orderStatus || exec.fillStatus || obj.status || '');

  if (!orderStatus) {
    if (cumQty > 0 && leavesQty === 0) orderStatus = 'filled';
    else if (cumQty > 0 && leavesQty > 0) orderStatus = 'partially_filled';
    else orderStatus = 'new';
  }

  const mapStatus = (s: string): Order['status'] => {
    const l = (s || '').toLowerCase();
    if (l === 'filled' || l === '2') return 'filled';
    if (l === 'partially_filled' || l === '1') return 'partially_filled';
    if (l === 'cancelled' || l === 'canceled' || l === '4') return 'cancelled';
    if (l === 'rejected' || l === '8') return 'rejected';
    return 'new';
  };

  // If backend sent a complete order, use it directly
  if (obj.order && typeof obj.order === 'object') {
    const completeOrder = obj.order;
    const orderId = completeOrder.id || clientOrderId;
    if (orderId) {
      cb.setOrders(prev => {
        const existingIndex = prev.findIndex(o => o.id === orderId);
        const orderFromBackend: Order = {
          id: orderId,
          exchangeOrderId: completeOrder.exchangeOrderId,
          symbol: completeOrder.symbol || symbol,
          side: (completeOrder.side || execSide).toLowerCase() as 'buy' | 'sell',
          qty: completeOrder.qty || Number(exec.quantity || obj.quantity || (cumQty + leavesQty) || execQty || 0),
          price: completeOrder.price || execPrice,
          status: mapStatus(completeOrder.status || orderStatus),
          time: completeOrder.time || fillTime,
          fills: completeOrder.fills || [],
          cumQty: completeOrder.cumQty ?? cumQty,
          leavesQty: completeOrder.leavesQty ?? leavesQty,
          avgFillPrice: completeOrder.avgFillPrice,
          lastFillTime: completeOrder.lastFillTime,
        };
        if (existingIndex >= 0) {
          const updated = [...prev];
          const existing = updated[existingIndex];
          updated[existingIndex] = {
            ...orderFromBackend,
            // preserve TIF fields from existing unless backend explicitly carries them
            timeInForce: orderFromBackend.timeInForce ?? existing.timeInForce,
            expireDate: orderFromBackend.expireDate ?? existing.expireDate,
            expireTime: orderFromBackend.expireTime ?? existing.expireTime,
          };
          return updated;
        }
        return [orderFromBackend, ...prev];
      });

      if (execPrice > 0 && execQty > 0) {
        _addTrade(symbol, execSide, execPrice, execQty, fillTime, cb);
      }
      return true;
    }
  }

  // Fallback: manual fill tracking
  if (clientOrderId && symbol && execPrice > 0 && execQty > 0) {
    cb.setOrders(prev => {
      const existingIndex = prev.findIndex(o => o.id === clientOrderId);
      const mappedStatus = mapStatus(orderStatus);
      const finalStatus = (leavesQty === 0 ? 'filled' : cumQty > 0 ? 'partially_filled' : mappedStatus) as Order['status'];

      if (existingIndex >= 0) {
        const updated = [...prev];
        const existingOrder = updated[existingIndex];
        const existingFills = existingOrder.fills || [];
        const previousCumQty = existingOrder.cumQty ?? 0;
        let fillQuantity = fillQty > 0 ? fillQty : (cumQty - previousCumQty);
        if (fillQuantity <= 0) fillQuantity = previousCumQty === 0 ? cumQty : execQty;

        const newFill: OrderFill = {
          fillId: exec.fillId || `${clientOrderId}_${fillTime}`,
          quantity: fillQuantity, price: execPrice, time: fillTime,
          cumQty, leavesQty, value: fillValue,
        };
        const isDuplicate = existingFills.some(f => f.time === fillTime && f.quantity === fillQuantity && f.price === execPrice);
        const updatedFills = isDuplicate ? existingFills : [...existingFills, newFill].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
        const totalValue = updatedFills.reduce((sum, f) => sum + (f.quantity * f.price), 0);
        const totalFilled = updatedFills.reduce((sum, f) => sum + f.quantity, 0);
        const avgFillPrice = totalFilled > 0 ? totalValue / totalFilled : undefined;
        updated[existingIndex] = { ...existingOrder, status: finalStatus, fills: updatedFills, cumQty, leavesQty, avgFillPrice, lastFillTime: fillTime };
        return updated;
      } else {
        const orderQty = Number(exec.quantity || obj.quantity || (cumQty + leavesQty) || execQty || 0);
        const newFill: OrderFill = {
          fillId: exec.fillId || `${clientOrderId}_${fillTime}`,
          quantity: execQty, price: execPrice, time: fillTime, cumQty, leavesQty, value: fillValue,
        };
        const newOrder: Order = {
          id: clientOrderId,
          exchangeOrderId: exec.orderId || obj.orderId || undefined,
          symbol, side: execSide, qty: orderQty, price: execPrice || 0, status: finalStatus,
          time: fillTime, fills: [newFill], cumQty, leavesQty,
          avgFillPrice: execQty > 0 ? execPrice : undefined, lastFillTime: fillTime,
        };
        return [newOrder, ...prev];
      }
    });

    _addTrade(symbol, execSide, execPrice, execQty, fillTime, cb);
  }
  return true;
}

function _addTrade(symbol: string, side: 'buy' | 'sell', price: number, qty: number, time: string, cb: MessageCallbacks) {
  const newTrade: Trade = { time, price, size: qty, side, symbol };
  const currTrades = cb.tradesBySecIdRef.current.get(symbol) || [];
  const updatedTrades = [newTrade, ...currTrades].slice(0, 100);
  cb.tradesBySecIdRef.current.set(symbol, updatedTrades);
  if (symbol === cb.selectedSymbol) {
    cb.setRecentTrades(updatedTrades.slice(0, 10));
    if (price > 0) cb.setCurrentPrice(price);
  }
}

export function handleOrderCancel(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.type === 'ORDER_CANCEL' || obj?.kind === 'ORDER_CANCEL')) return false;

  const cancelData = obj.order || obj;
  const orderId = cancelData.orderId || cancelData.clientOrderId || cancelData.order_id || '';

  if (!orderId) return false;

  cb.setOrders((prev: Order[]) =>
    prev.map(o =>
      (o.id === orderId || o.exchangeOrderId === orderId)
        ? { ...o, status: 'cancelled' as Order['status'] }
        : o
    )
  );

  return true;
}
